import React from "react";
import { useTranslation } from "react-i18next";
import { UcWidget } from "../common/UcWidget";
// @ts-ignore
import { UcHint } from "../common/UcHint";
import { useAggBillingCycleReadings } from "../aggReadings/AggBillingCycleReadingsApiEndpoints";
import { SwrOptions } from "../../lib/useGenericSWR.interfaces";
import useOrgAppSettings from 'lib/useOrgAppSettings';
import NeighborhoodCompareWidgetChart from "./NeighborhoodCompareWidgetChart";
// @ts-ignore
import { DateTime } from 'luxon';
// @ts-ignore
import { useBillPeriods } from '../readings/BillPeriodAPIEndpoint';
import { UcLoading } from "../common/UcLoading";
import { Account } from "../../lib/AccountProvider";
import { Alert, Box, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material"; import CircleIcon from '@mui/icons-material/Circle';
import { UcMeterSelectModular } from "components/common/UcMeterSelectModular";
// @ts-ignore
import { useMyTimezone } from "components/customer/orgs/MyOrgAPIEndpoints";
interface NeighborhoodCompareWidgetProps {
    account: Account;
    orgMeasurementUnit: string;
}

function NeighborhoodCompareWidget(props: Readonly<NeighborhoodCompareWidgetProps>) {
    const { account } = props;
    const { orgAppSettings } = useOrgAppSettings();
    const orgMeasurementUnit = props.orgMeasurementUnit === 'Metric' ? 'meters' : 'feet';
    const [currentBillingCycle, setCurrentBillingCycle] = React.useState<any>();
    const [uom, setUom] = React.useState('KWH');
    const { t } = useTranslation();

    const minDate = account?.minStartTimestamp == null ? new Date() : new Date(account?.minStartTimestamp);
    const maxDate = account?.maxEndTimestamp == null ? new Date() : new Date(account?.maxEndTimestamp);

    const billCyclesFilter = {
        take: 100,
        skip: 0,
        excludeFutureStart: true,
        sort: [{ field: "StartTimestamp", sort: "desc" }],
        generalSearch: "",
        startTimeUTC: DateTime.fromJSDate(minDate).toUTC().toISO(),
        endTimeUTC: DateTime.fromJSDate(maxDate).toUTC().toISO(),
        accountId: account.id,
        status: "ACTIVE",
        uom: uom
    };

    const billingCycles = useBillPeriods(billCyclesFilter, account.id !== null);
    React.useEffect(() => {
        if (billingCycles?.result?.length > 0) {
            setCurrentBillingCycle(billingCycles?.result[1]);
        }
    }, [billingCycles?.result, billingCycles?.result?.length]);

    const dataWarehouseSwr = useAggBillingCycleReadings(account.id, uom, currentBillingCycle?.startDateTimeUtc, currentBillingCycle?.endDateTimeUtc, 'RESIDENTIAL', {} as SwrOptions);

    const timezone = useMyTimezone();

    const startDate = React.useMemo(() =>
        DateTime.fromISO(currentBillingCycle?.startDateTimeUtc, { zone: timezone })
            .toJSDate(), [currentBillingCycle?.startDateTimeUtc, timezone]);

    const endDate = React.useMemo(() => 
        DateTime.fromISO(currentBillingCycle?.endDateTimeUtc, { zone: timezone })
            .toJSDate(), [currentBillingCycle?.endDateTimeUtc, timezone]);
    
    const hintTitle = t(`How do I compare?`);
    const hintText = t(`We compared your most recent billing cycle usage to other premises in your area.`);

    const handleChangeCommodityType = (meterOption: any) => {
        setUom(meterOption.uom);
    };

    const getSafeMeasurement = (meters: number) => {
        let measurement;

        if (orgMeasurementUnit === 'feet') {
            measurement = (meters * 3.28084);
        }
        else {
            measurement = meters;
        }


        return measurement?.toFixed(2);
    }

    const actionItems: any[] = [
        {
            component: (
                <UcHint
                    hintTitle={hintTitle}
                    hintText={hintText}
                >
                    <List dense={false}>
                        <ListItem sx={{ pl: 0, py: 0, alignItems: 'flex-start' }}>
                            <ListItemAvatar sx={{ minWidth: 22, mt: '2px' }}>
                                <CircleIcon sx={{ fontSize: "small" }} color={'primary'} />
                            </ListItemAvatar>
                            <ListItemText sx={{ mt: 0 }}
                                primary={t(`The nearest compared premise was {{minDistanceFromCenterPoint}} {{orgMeasurementUnit}} away.`,
                                    {
                                        minDistanceFromCenterPoint: getSafeMeasurement(dataWarehouseSwr?.result?.minDistanceFromCenterPoint) ?? "---",
                                        orgMeasurementUnit: orgMeasurementUnit
                                    })}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0, py: 0, alignItems: 'flex-start' }}>
                            <ListItemAvatar sx={{ minWidth: 22, mt: '2px' }}>
                                <CircleIcon sx={{ fontSize: "small" }} color={'primary'} />
                            </ListItemAvatar>
                            <ListItemText sx={{ mt: 0 }}
                                primary={t(`The farthest compared premise was {{maxDistanceFromCenterPoint}} {{orgMeasurementUnit}} away.`,
                                    {
                                        maxDistanceFromCenterPoint: getSafeMeasurement(dataWarehouseSwr?.result?.maxDistanceFromCenterPoint) ?? "---",
                                        orgMeasurementUnit: orgMeasurementUnit
                                    })}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0, py: 0, alignItems: 'flex-start' }}>
                            <ListItemAvatar sx={{ minWidth: 22, mt: '2px' }}>
                                <CircleIcon sx={{ fontSize: "small" }} color={'primary'} />
                            </ListItemAvatar>
                            <ListItemText sx={{ mt: 0 }}
                                primary={t(`For an average distance of {{avgDistanceFromCenterPoint}} {{orgMeasurementUnit}}.`,
                                    {
                                        avgDistanceFromCenterPoint: getSafeMeasurement(dataWarehouseSwr?.result?.avgDistanceFromCenterPoint) ?? "---",
                                        orgMeasurementUnit: orgMeasurementUnit
                                    })}
                            />
                        </ListItem>
                    </List>
                </UcHint>
            ),
        },
        {
            component: (
                <UcMeterSelectModular
                    forcedWidth={"75%"}
                    uom={uom}
                    onChange={handleChangeCommodityType}
                    onlyCommodity={true}
                    startDate={startDate}
                    endDate={endDate}
                    datacyid={"neighborhood_compare_dashboard_widget_meter_select"}
                />
            )
        }
    ];

    return (
        <UcWidget
            actionItems={actionItems}
            minHeight={"auto"}
            title={t("Neighborhood Comparison")}
            alignTitleLeft={true}
            contentPadding={"0"}
        >
            <UcLoading isLoading={dataWarehouseSwr.isLoading} hasFade={false} height={'20vh'}>
                {dataWarehouseSwr.result?.totalCount > 0 ? (
                    <NeighborhoodCompareWidgetChart
                        currentBillingCycle={currentBillingCycle}
                        dataWarehouse={dataWarehouseSwr}
                        uom={uom}
                        uomDisplay={orgAppSettings?.result?.uomMappings[uom].safeUomDisplay}
                    />
                ) : (
                    <Box sx={{ mt: 2, mb: 2, pl: "1rem", pr: "1rem" }}>
                        <Alert
                            severity="warning" data-cy-id={'neighborhood_compare_dashboard_widget_no_data'}>
                            {t("No data available.")}
                        </Alert>
                    </Box>
                )}
            </UcLoading>
        </UcWidget>
    );
}

export default NeighborhoodCompareWidget;